import { useContext, useState } from "react";
import { Box, Typography, Button, Avatar, AvatarGroup, CircularProgress } from "@mui/material";
import { NavLink } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import HomeRepairServiceOutlinedIcon from "@mui/icons-material/HomeRepairServiceOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import SpeedOutlinedIcon from "@mui/icons-material/SpeedOutlined";
import WorkOutlineOutlinedIcon from "@mui/icons-material/WorkOutlineOutlined";

import { AppContext } from "../../Context/AppContext";
import CustomTooltip from "../../Components/CustomTooltip";
import CustomFilterTabs from "../../Components/CustomFilterTabs";
// Reuse the presentational pieces built for the Community Manager dashboard —
// same visual language, no duplication.
import StatCard from "../Dashboard/components/StatCard";
import TopFiveTable from "../Dashboard/components/TopFiveTable";
import SetupCard from "../Dashboard/components/SetupCard";
import Sparkline from "../Dashboard/components/Sparkline";
import HealthStat from "../Dashboard/components/HealthStat";
import { useSupplierDashboard, LONG_OVERDUE_DAYS } from "./hooks/useSupplierDashboard";
import {
  dashboardStyles as d,
  statCardStyles as sc,
  topFiveStyles as tf,
} from "../Dashboard/styles/styles";

const fmt = (n) => Number(n).toLocaleString("en-US");
const listTitleIcon = { fontSize: 18, color: "rgba(47, 43, 61, 0.55)" };
const loadingBoxSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  minHeight: "60vh",
};

const CARD_BORDER = "1px solid rgba(234, 234, 234, 1)";
// Rows shown per not-yet-active list; "View all" opens the full tab.
const MAX_PENDING_ROWS = 3;
const AMBER = { color: "rgba(180, 120, 20, 1)", bg: "rgba(255, 244, 229, 1)" };
const GREEN = { color: "rgba(36, 139, 112, 1)", bg: "rgba(219, 240, 236, 1)" };
const NEUTRAL = { color: "rgba(62, 63, 76, 0.7)", bg: "rgba(242, 243, 251, 1)" };

const TONES = {
  good: { color: "rgba(36, 139, 112, 1)", bg: "rgba(219, 240, 236, 1)" },
  warn: { color: "rgba(180, 120, 20, 1)", bg: "rgba(255, 244, 229, 1)" },
  bad: { color: "rgba(200, 55, 55, 1)", bg: "rgba(252, 235, 235, 1)" },
  neutral: { color: "rgba(62, 63, 76, 0.7)", bg: "rgba(242, 243, 251, 1)" },
};
const JOB_TONES = {
  awaiting_approval: AMBER,
  in_progress: GREEN,
  scheduled: NEUTRAL,
  // Set client-side: a dated job whose day has passed (see the hook).
  overdue: TONES.bad,
};
const tier = (value, good, warn, higherBetter) => {
  // No data earns no colour — a missing metric is not a bad one.
  if (value === null || value === undefined) return "neutral";
  if (higherBetter) return value >= good ? "good" : value >= warn ? "warn" : "bad";
  return value <= good ? "good" : value <= warn ? "warn" : "bad";
};

// Local styles for the supplier-only sections (action tiles + communities).
const a = {
  tileGrid: {
    display: "grid",
    gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "repeat(3, minmax(0, 1fr))" },
    gap: "12px",
  },
  tile: {
    border: CARD_BORDER,
    borderRadius: "12px",
    backgroundColor: "#fff",
    padding: "16px",
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    height: "100%",
  },
  tileLabelRow: { display: "flex", alignItems: "center", gap: "6px" },
  tileLabel: { fontSize: "13px", fontWeight: 400, color: "rgba(71, 84, 103, 1)" },
  tileValue: { fontSize: "30px", fontWeight: 600, lineHeight: "120%" },
  tileLink: {
    fontSize: "13px",
    fontWeight: 500,
    color: "rgba(36, 139, 112, 1)",
    textDecoration: "underline",
    cursor: "pointer",
    marginTop: "6px",
  },
  chip: {
    display: "inline-block",
    fontSize: "12px",
    fontWeight: 500,
    padding: "2px 10px",
    borderRadius: "10px",
    whiteSpace: "nowrap",
  },
  commCard: {
    border: CARD_BORDER,
    borderRadius: "12px",
    backgroundColor: "#fff",
    padding: "16px",
    display: "flex",
    flexDirection: "column",
    gap: "14px",
  },
  commTopRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    flexWrap: "wrap",
  },
  pendingText: { fontSize: "13px", color: "rgba(62, 63, 76, 0.9)", minWidth: 0 },
  findBtn: {
    textTransform: "none",
    fontSize: "13px",
    fontWeight: 500,
    color: "rgba(36, 139, 112, 1)",
    border: "1px solid rgba(36, 139, 112, 0.5)",
    borderRadius: "8px",
    padding: "5px 14px",
    whiteSpace: "nowrap",
    "&:hover": { backgroundColor: "rgba(36, 139, 112, 0.06)", border: "1px solid rgba(36, 139, 112, 0.5)" },
  },
  joinBtn: {
    textTransform: "none",
    fontSize: "12px",
    fontWeight: 500,
    color: "#fff",
    backgroundColor: "rgba(36, 139, 112, 1)",
    borderRadius: "8px",
    padding: "3px 14px",
    boxShadow: "none",
    whiteSpace: "nowrap",
    "&:hover": { backgroundColor: "rgba(30, 118, 95, 1)", boxShadow: "none" },
  },
  // Same size as joinBtn, outlined — the secondary answer next to it.
  outlineSmallBtn: {
    textTransform: "none",
    fontSize: "12px",
    fontWeight: 500,
    color: "rgba(36, 139, 112, 1)",
    border: "1px solid rgba(36, 139, 112, 0.5)",
    borderRadius: "8px",
    padding: "2px 14px",
    whiteSpace: "nowrap",
    "&:hover": { backgroundColor: "rgba(36, 139, 112, 0.06)", border: "1px solid rgba(36, 139, 112, 0.5)" },
  },
  // Long-overdue "was it done?" strip under an upcoming-jobs row.
  overduePrompt: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    flexWrap: "wrap",
    padding: "8px 12px",
    borderRadius: "8px",
    backgroundColor: TONES.bad.bg,
  },
  overdueQuestion: { fontSize: "13px", fontWeight: 500, color: TONES.bad.color },
  // Header of a not-yet-active list (waiting approval / invitations).
  listHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "10px",
  },
  listTitle: { fontSize: "13px", fontWeight: 600, color: "rgba(47, 43, 61, 0.9)" },
  // The second list in the card is divided from the first.
  listDivider: { paddingTop: "14px", borderTop: CARD_BORDER },
};

const SupplierDashboardContainer = () => {
  const { t, i18n } = useTranslation("page-supplier-dashboard");
  const { sidebarData } = useContext(AppContext);
  const lang = (i18n.language || "en").toLowerCase().startsWith("he") ? "he" : "en";
  const loc = (v) =>
    v && typeof v === "object" && !Array.isArray(v) ? v[lang] ?? v.en : v;
  const locPoints = (pts) =>
    pts.map((p) => ({ value: p.value, label: loc(p.label), full: loc(p.full) }));
  const supplierName = sidebarData?.supplierName || "";
  const supplierImage = sidebarData?.supplierImage || "";
  const { data: m, isLoading, error } = useSupplierDashboard();
  // Every hook must run before the loading/error return below, so this one
  // takes a static default and `activeType` reconciles it against the tabs the
  // supplier actually has once the data lands.
  const [qualityType, setQualityType] = useState("services");

  if (isLoading || (!m && !error)) {
    return (
      <Box sx={{ ...d.page, ...loadingBoxSx }}>
        <CircularProgress sx={{ color: "rgba(36, 139, 112, 1)" }} />
      </Box>
    );
  }

  if (error || !m) {
    return (
      <Box sx={{ ...d.page, ...loadingBoxSx }}>
        <Typography sx={{ color: "rgba(62, 63, 76, 0.7)" }}>
          {t("load_error")}
        </Typography>
      </Box>
    );
  }

  const cur = m.currencySymbol || "₪";
  const money = (n) => `${cur}${fmt(n)}`;
  // Nothing to withdraw at ₪0 (or no data). With a positive balance the button
  // is enabled but still has no action — payouts need a ledger first.
  const canWithdraw = m.earnings.currentBalance > 0;
  const withdrawBtn = (
    <Button sx={sc.withdrawBtn} variant="contained" disableElevation disabled={!canWithdraw}>
      {t("withdraw")}
    </Button>
  );

  // Which offering types this supplier actually has drives the adaptive areas.
  const qualityTabs = [
    m.hasServices ? "services" : null,
    m.hasProducts ? "products" : null,
  ].filter(Boolean);
  const activeType = qualityTabs.includes(qualityType)
    ? qualityType
    : qualityTabs[0] || "services";

  // A supplier with no orders yet divides by zero — show 0, not NaN.
  const avg = (total, count) => (count ? Math.round(total / count) : 0);
  const avgAll = avg(m.sales.moneyAllTime, m.sales.countAllTime);
  const avgMonth = avg(m.sales.moneyThisMonth, m.sales.countThisMonth);

  const infoTip = (text) => (
    <CustomTooltip title={text}>
      <Box component="span" sx={sc.tooltipTrigger}>
        <Box
          component="img"
          src="/icons/question-circle-icon.svg"
          alt=""
          sx={sc.tooltipIcon}
        />
      </Box>
    </CustomTooltip>
  );

  const sectionHeader = (title, tip) => (
    <Box sx={d.sectionHeader}>
      <Typography sx={d.sectionTitle}>{title}</Typography>
      {tip ? infoTip(tip) : null}
    </Box>
  );

  // Top-list name cell: rank + thumbnail + name.
  const nameCell = (item, index) => (
    <Box sx={tf.nameCell}>
      <Box component="span" sx={tf.rank}>
        {index + 1}
      </Box>
      <Box sx={tf.avatar}>
        {item.image ? (
          <Box component="img" src={item.image} alt="" sx={tf.avatarImg} />
        ) : (
          loc(item.name).charAt(0)
        )}
      </Box>
      <span>{loc(item.name)}</span>
    </Box>
  );

  const toned = (text, level) => (
    <Box component="span" sx={{ color: TONES[level].color, fontWeight: 500 }}>
      {text}
    </Box>
  );

  const statusChip = (status) => (
    <Box
      component="span"
      sx={{ ...a.chip, color: JOB_TONES[status].color, backgroundColor: JOB_TONES[status].bg }}
    >
      {t(`jobStatus.${status}`)}
    </Box>
  );

  // ---- Needs-action layer ------------------------------------------------
  const actionTiles = [
    {
      key: "pending",
      value: m.actions.pendingApproval,
      label: t("action.pending"),
      tip: t("action.pending_tip"),
      accent: m.actions.pendingApproval > 0 ? AMBER.color : "rgba(47, 43, 61, 0.9)",
      showReview: m.actions.pendingApproval > 0,
    },
    {
      key: "in_progress",
      value: m.actions.inProgress,
      label: t("action.in_progress"),
      accent: "#7367F0",
    },
    {
      key: "due_today",
      value: m.actions.dueToday,
      label: t("action.due_today"),
      accent: GREEN.color,
    },
    // Overdue is its own tile, and only when there is something to show. It is
    // deliberately not folded into "due today": a job that slipped last month
    // is a different problem from one due in a few hours, and merging them
    // makes both numbers meaningless.
    ...(m.actions.overdue > 0
      ? [
          {
            key: "overdue",
            value: m.actions.overdue,
            label: t("action.overdue"),
            tip: t("action.overdue_tip"),
            accent: TONES.bad.color,
            showReview: true,
            reviewLabel: t("action.review_overdue"),
          },
        ]
      : []),
  ];

  // An overdue job reads "DD/MM/YYYY · N days ago"; the date and the number are
  // isolated so they keep their order inside Hebrew text.
  const whenCell = (row) =>
    row.overdueDays ? (
      <>
        <bdi>{row.dueDate}</bdi>
        {" · "}
        <Trans
          t={t}
          i18nKey="when.days_ago"
          count={row.overdueDays}
          values={{ count: row.overdueDays }}
          components={{ n: <bdi /> }}
        />
      </>
    ) : (
      loc(row.when)
    );

  // Long-overdue accepted work asks whether it actually happened. Both answers
  // open the job in My Jobs (the same window its View button opens), where
  // complete / request payment and cancel already live — no new endpoints.
  // A job still awaiting the supplier's approval was never accepted, so there
  // is nothing to have "done"; it keeps the plain overdue chip.
  const jobLink = (row) =>
    `/my-jobs?job=${encodeURIComponent(row.id)}&jobType=${row.itemType}&view=active`;
  const longOverduePrompt = (row) =>
    row.overdueDays >= LONG_OVERDUE_DAYS && row.baseStatus !== "awaiting_approval" ? (
      <Box sx={a.overduePrompt}>
        <Typography sx={a.overdueQuestion}>{t("overdue_prompt.question")}</Typography>
        <Box sx={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
          <Button component={NavLink} to={jobLink(row)} sx={a.joinBtn}>
            {t("overdue_prompt.done")}
          </Button>
          <Button component={NavLink} to={jobLink(row)} sx={a.outlineSmallBtn}>
            {t("overdue_prompt.canceled")}
          </Button>
        </Box>
      </Box>
    ) : null;

  const jobColumns = [
    {
      label: t("cols.job"),
      // Same short id My Jobs shows, so the supplier can match the row there.
      render: (row) => (
        <CustomTooltip title={row.id}>
          <Box component="span" sx={tf.count}>
            #{String(row.id).slice(0, 4)}
          </Box>
        </CustomTooltip>
      ),
    },
    { label: t("cols.community"), render: (row) => <span>{loc(row.community)}</span> },
    { label: t("cols.when"), render: (row) => <Box component="span" sx={tf.count}>{whenCell(row)}</Box> },
    { label: t("cols.status"), align: "right", render: (row) => statusChip(row.status) },
  ];

  // ---- Communities not active yet ------------------------------------------
  // 'request' = the supplier asked to join and waits for the community;
  // 'invite'  = the community invited the supplier, who has to answer.
  const requests = m.communities.pending.filter((c) => c.type !== "invite");
  const invites = m.communities.pending.filter((c) => c.type === "invite");
  const pendingList = ({ titleKey, items, tab, action, divided }) => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "10px",
        ...(divided ? a.listDivider : {}),
      }}
    >
      <Box sx={a.listHeader}>
        <Typography sx={a.listTitle}>
          <Trans
            t={t}
            i18nKey={titleKey}
            values={{ count: items.length }}
            components={{ n: <bdi /> }}
          />
        </Typography>
        <Typography
          component={NavLink}
          to={`/my-communities?tab=${tab}`}
          sx={{ ...a.tileLink, marginTop: 0 }}
        >
          {t("communities.view_all")}
        </Typography>
      </Box>
      {items.slice(0, MAX_PENDING_ROWS).map((c) => (
        <Box key={c.id} sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <Box sx={tf.avatar}>
            {c.image ? (
              <Box component="img" src={c.image} alt="" sx={tf.avatarImg} />
            ) : (
              loc(c.name).charAt(0)
            )}
          </Box>
          <Typography sx={a.pendingText}>
            <Box component="span" sx={{ fontWeight: 500 }}>
              {loc(c.name)}
            </Box>
            {" · "}
            {t("communities.members", { count: c.members })}
          </Typography>
          <Box sx={{ marginInlineStart: "auto" }}>{action(c)}</Box>
        </Box>
      ))}
    </Box>
  );

  // ---- Top performers (only lists the supplier has content for) ----------
  const ordersCol = {
    label: t("cols.orders"),
    align: "right",
    render: (row) => (
      <Box component="span" sx={tf.count}>
        {fmt(row.count)}
      </Box>
    ),
  };
  const revenueCol = {
    label: t("cols.revenue"),
    align: "right",
    render: (row) => (
      <Box component="span" sx={tf.money}>
        {money(row.revenue)}
      </Box>
    ),
  };

  const topTables = [
    m.hasServices && m.topServices.length
      ? {
          key: "services",
          icon: <HomeRepairServiceOutlinedIcon sx={listTitleIcon} />,
          title: t("top.services"),
          tip: t("top.tips.services"),
          rows: m.topServices,
          columns: [
            { label: t("cols.service"), render: (row, i) => nameCell(row, i) },
            ordersCol,
            revenueCol,
          ],
        }
      : null,
    m.hasProducts && m.topProducts.length
      ? {
          key: "products",
          icon: <Inventory2OutlinedIcon sx={listTitleIcon} />,
          title: t("top.products"),
          tip: t("top.tips.products"),
          rows: m.topProducts,
          columns: [
            { label: t("cols.product"), render: (row, i) => nameCell(row, i) },
            ordersCol,
            revenueCol,
          ],
        }
      : null,
    m.topCommunities.length
      ? {
          key: "communities",
          icon: <StorefrontOutlinedIcon sx={listTitleIcon} />,
          title: t("top.communities"),
          tip: t("top.tips.communities"),
          rows: m.topCommunities,
          columns: [
            { label: t("cols.community"), render: (row, i) => nameCell(row, i) },
            revenueCol,
          ],
        }
      : null,
  ].filter(Boolean);

  const topGridSx = {
    ...d.topGrid,
    gridTemplateColumns: {
      xs: "minmax(0, 1fr)",
      md: `repeat(${topTables.length}, minmax(0, 1fr))`,
    },
  };

  // ---- Order quality -----------------------------------------------------
  const isServices = activeType === "services";
  const q = m.quality[activeType];
  // A metric is null when the supplier has no orders in that window — render a
  // dash rather than a confident "0%", which would read as a failing score.
  const NO_DATA = "—";
  const fmtUnit = (v, unit) =>
    v === null || v === undefined
      ? NO_DATA
      : unit === "hours"
        ? `${v} ${t("sq.hours")}`
        : unit === "days"
          ? `${v} ${t("sq.days")}`
          : `${v}%`;
  const metricConfig = [
    { key: "fulfillment", unit: "percent", labelKey: "sq.fulfillment_rate", tipKey: "sq.tips.fulfillment", good: 90, warn: 75, higherBetter: true },
    { key: "approval", unit: "hours", labelKey: isServices ? "sq.avg_response" : "sq.avg_approval", tipKey: isServices ? "sq.tips.response" : "sq.tips.approval", good: 4, warn: 12, higherBetter: false },
    { key: "completion", unit: "days", labelKey: isServices ? "sq.avg_completion" : "sq.avg_delivery", tipKey: isServices ? "sq.tips.completion" : "sq.tips.delivery", good: 2, warn: 4, higherBetter: false },
    { key: "cancellation", unit: "percent", labelKey: "sq.cancellation_rate", tipKey: "sq.tips.cancellation", good: 5, warn: 12, higherBetter: false },
  ];
  const healthMetrics = metricConfig.map((c) => {
    const md = q.metrics[c.key];
    return {
      label: t(c.labelKey),
      value: fmtUnit(md.raw, c.unit),
      level: tier(md.raw, c.good, c.warn, c.higherBetter),
      tip: t(c.tipKey),
      trend: md.trend.map(([month, v]) => [loc(month), fmtUnit(v, c.unit)]),
    };
  });
  const qualityColumns = [
    { label: t("cols.community"), render: (row, i) => nameCell(row, i) },
    { label: t("cols.fulfillment"), align: "right", render: (row) => toned(`${row.fulfillment}%`, tier(row.fulfillment, 90, 75, true)) },
    { label: t(isServices ? "cols.response" : "cols.approval"), align: "right", render: (row) => (
      <Box component="span" sx={tf.count}>
        {row.approvalHours === null || row.approvalHours === undefined
          ? NO_DATA
          : `${row.approvalHours} ${t("sq.hours")}`}
      </Box>
    ) },
    { label: t("cols.cancellations"), align: "right", render: (row) => toned(`${row.cancellationRate}%`, tier(row.cancellationRate, 5, 12, false)) },
  ];

  return (
    <Box sx={d.page}>
      <Box sx={d.header}>
        <Avatar src={supplierImage} alt={supplierName} sx={d.headerLogo}>
          {supplierName.charAt(0)}
        </Avatar>
        <Typography sx={d.title}>
          {supplierName
            ? t("page_title", { name: supplierName })
            : t("page_title_fallback")}
        </Typography>
      </Box>

      {/* --- Needs you now: the operational layer, leading the dashboard --- */}
      {sectionHeader(t("sections.action"))}
      <Box
        sx={{
          ...a.tileGrid,
          // 3 tiles normally, 4 when the supplier has overdue work.
          gridTemplateColumns: {
            xs: "minmax(0, 1fr)",
            sm: `repeat(${actionTiles.length}, minmax(0, 1fr))`,
          },
        }}
      >
        {actionTiles.map((tile) => (
          <Box key={tile.key} sx={a.tile}>
            <Box sx={a.tileLabelRow}>
              <Typography sx={a.tileLabel}>{tile.label}</Typography>
              {tile.tip ? infoTip(tile.tip) : null}
            </Box>
            <Typography sx={{ ...a.tileValue, color: tile.accent }}>
              {fmt(tile.value)}
            </Typography>
            {tile.showReview && (
              <Typography component={NavLink} to="/my-jobs" sx={a.tileLink}>
                {tile.reviewLabel || t("action.review")}
              </Typography>
            )}
          </Box>
        ))}
      </Box>
      <TopFiveTable
        emptyText={t("sq.no_data")}
        icon={<WorkOutlineOutlinedIcon sx={listTitleIcon} />}
        title={t("action.upcoming_title")}
        tooltip={infoTip(t("action.upcoming_tip"))}
        columns={jobColumns}
        rows={m.upcomingJobs}
        renderRowFooter={longOverduePrompt}
      />

      {/* --- Money --- */}
      {sectionHeader(t("sections.money"))}
      <Box sx={d.chartGrid}>
        <StatCard
          icon={<ShoppingBagOutlinedIcon sx={{ color: "#7367F0", fontSize: 24 }} />}
          iconBg="rgba(242, 243, 251, 1)"
          label={t("cards.sales")}
          tooltip={infoTip(t("tooltips.sales"))}
          hideIconOnMobile
        >
          <Typography sx={sc.amount}>{money(m.sales.moneyAllTime)}</Typography>
          <Typography sx={sc.subline}>
            {t("sales_alltime", {
              count: fmt(m.sales.countAllTime),
              avg: money(avgAll),
            })}
          </Typography>
          <Typography sx={sc.subline}>
            {t("sales_month", {
              money: money(m.sales.moneyThisMonth),
              count: fmt(m.sales.countThisMonth),
              avg: money(avgMonth),
            })}
          </Typography>
          <Sparkline
            points={locPoints(m.sales.monthly)}
            color="#7367F0"
            fill="rgba(115, 103, 240, 0.12)"
            formatValue={money}
          />
        </StatCard>

        <StatCard
          icon={
            <PaymentsOutlinedIcon
              sx={{ color: "rgba(36, 139, 112, 1)", fontSize: 24 }}
            />
          }
          iconBg="rgba(219, 240, 236, 1)"
          label={t("cards.earnings")}
          tooltip={infoTip(t("tooltips.earnings"))}
          hideIconOnMobile
          // currentBalance is the DB sum of the supplier's share totals (null
          // only with no rev-share rows); there is no payout ledger yet.
          // Withdraw is disabled with an explanatory tooltip unless the
          // balance is > 0, and even then it is still wired to nothing. The
          // amount falls back to a dash, because `money(null)` renders "₪NaN".
          headerRight={
            <Box sx={sc.walletTop}>
              <Box sx={sc.balanceBox}>
                <Typography sx={sc.balanceLabel}>
                  {t("current_balance")}
                </Typography>
                <Typography sx={sc.balanceValue}>
                  {m.earnings.currentBalance === null
                    ? NO_DATA
                    : money(m.earnings.currentBalance)}
                </Typography>
              </Box>
              {canWithdraw ? withdrawBtn : (
                <CustomTooltip title={t("withdraw_unavailable_tip")}>
                  <Box component="span" sx={sc.withdrawTipTrigger}>
                    {withdrawBtn}
                  </Box>
                </CustomTooltip>
              )}
            </Box>
          }
        >
          <Typography sx={sc.amount}>{money(m.earnings.allTime)}</Typography>
          <Typography sx={sc.subline}>{t("earn_alltime")}</Typography>
          <Typography sx={sc.subline}>
            {t("earn_month", { money: money(m.earnings.thisMonth) })}
          </Typography>
          <Sparkline
            points={locPoints(m.earnings.monthly)}
            color="rgba(36, 139, 112, 1)"
            fill="rgba(36, 139, 112, 0.12)"
            formatValue={money}
          />
        </StatCard>
      </Box>

      {/* --- Communities & growth --- */}
      {sectionHeader(t("sections.communities"))}
      <Box sx={a.commCard}>
        <Box sx={a.commTopRow}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <Box
              sx={{
                ...sc.iconBox("rgba(255, 244, 229, 1)"),
                width: "44px",
                height: "44px",
                minWidth: "44px",
              }}
            >
              <GroupsOutlinedIcon sx={{ color: "#ff9f43", fontSize: 22 }} />
            </Box>
            <Box>
              <Typography sx={sc.statNum}>{fmt(m.communities.active)}</Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <Typography sx={{ fontSize: "13px", color: "rgba(71, 84, 103, 1)" }}>
                  {t("communities.active")}
                </Typography>
                {infoTip(t("communities.active_tip"))}
              </Box>
            </Box>
            {/* The API sends up to four active-community images, no names. */}
            {m.communities.activeAvatars?.length > 0 && (
              <AvatarGroup
                max={4}
                sx={{ "& .MuiAvatar-root": { width: 28, height: 28, fontSize: 12, border: "2px solid #fff" } }}
              >
                {m.communities.activeAvatars.map((src, i) => (
                  <Avatar key={i} src={src} alt="" />
                ))}
              </AvatarGroup>
            )}
          </Box>
          <Button component={NavLink} to="/find-communities" sx={a.findBtn}>
            {t("communities.find_cta")}
          </Button>
        </Box>

      </Box>

      {/* Not-yet-active relationships live outside the "active" card so its
          count and its contents agree (COMY1-401). */}
      {(requests.length > 0 || invites.length > 0) && (
        <Box sx={a.commCard}>
          {requests.length > 0 &&
            pendingList({
              titleKey: "communities.waiting_title",
              items: requests,
              tab: "waiting-approval",
              action: () => (
                <Box component="span" sx={{ ...a.chip, color: NEUTRAL.color, backgroundColor: NEUTRAL.bg }}>
                  {t("communities.pending_label")}
                </Box>
              ),
            })}
          {invites.length > 0 &&
            pendingList({
              titleKey: "communities.invites_title",
              items: invites,
              tab: "invites",
              divided: requests.length > 0,
              // `invite_community_id` puts My Communities on the Join requests
              // tab and pops the offer prompt for THIS community. It also
              // bypasses the once-per-request suppression there, so coming
              // from the dashboard prompts every time the invitation is still
              // unanswered — while simply opening the tab does not.
              action: (c) => (
                <Button
                  component={NavLink}
                  to={`/my-communities?invite_community_id=${c.id}`}
                  sx={a.joinBtn}
                >
                  {t("communities.join")}
                </Button>
              ),
            })}
        </Box>
      )}

      {/* --- Analytics: top performers --- */}
      {topTables.length > 0 && sectionHeader(t("sections.top"))}
      {topTables.length > 0 && (
        <Box sx={topGridSx}>
          {topTables.map((tt) => (
            <TopFiveTable
        emptyText={t("sq.no_data")}
              key={tt.key}
              icon={tt.icon}
              title={tt.title}
              tooltip={infoTip(tt.tip)}
              columns={tt.columns}
              rows={tt.rows}
            />
          ))}
        </Box>
      )}

      {/* --- Analytics: order quality --- */}
      <Box sx={d.qualityHeaderRow}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <Typography sx={d.sectionTitle}>{t("sections.quality")}</Typography>
          {infoTip(t("sections.quality_tip"))}
        </Box>
        {qualityTabs.length > 1 && (
          <Box sx={{ minWidth: 0, maxWidth: "100%" }}>
            <CustomFilterTabs
              tabs={[
                { label: t("sq.tab_services"), value: "services" },
                { label: t("sq.tab_products"), value: "products" },
              ]}
              activeTab={activeType}
              onChange={(_, v) => setQualityType(v)}
            />
          </Box>
        )}
      </Box>
      <Box sx={d.qualityGrid}>
        <Box sx={d.healthGrid}>
          {healthMetrics.map((h, i) => (
            <HealthStat
              key={i}
              label={h.label}
              value={h.value}
              tone={TONES[h.level]}
              // No data earns no pill. `sq.status` only defines good/warn/bad —
              // a "neutral" lookup would render the raw key next to a "—".
              statusText={h.level === "neutral" ? null : t(`sq.status.${h.level}`)}
              tooltip={infoTip(h.tip)}
              trend={h.trend}
            />
          ))}
        </Box>
        <TopFiveTable
        emptyText={t("sq.no_data")}
          icon={<SpeedOutlinedIcon sx={listTitleIcon} />}
          title={t("sq.community_table_title")}
          tooltip={infoTip(t("sq.community_table_tip"))}
          columns={qualityColumns}
          rows={q.communities}
        />
      </Box>

      <SetupCard />
    </Box>
  );
};

export default SupplierDashboardContainer;
